import { useState, useEffect } from "react";
import { useSelector } from "react-redux";

// Hooks
import { gql, useSubscriptions } from "@/Common/context/subscriptions/Subscriptions";
import { useLocations } from "@/Common/hooks/Location/useLocations";

// Layouts
import AppSideBar from "@/Layouts/components/SideBars/AppSideBar";
import BodyLayer from "@/Layouts/components/Layers/BodyLayer";
import CollapsibleLeftPanel from "@/Layouts/components/CollapsibleLeftPanel";
import RoundedBox from "@/Layouts/components/Basics/RoundedBox";

// Components
import GroupAccessSettingsModel from "./GroupAccessSettingsModel";
import useCacheCleaner from "@/Common/hooks/CacheCleaner";

const QUERY_IDENTIFIERS = ["accessAuthorizationGroups"];

export default function GroupAccessSettingsLayer() {
	const { clearCache } = useCacheCleaner();
	const tenant = "0000100000";
	const { globalLocations, isLoading } = useLocations(tenant);
	const [selectedLocation, setSelectedLocation] = useState(null);
	const [open, setOpen] = useState(true);
	const { addSubscription, clearSubscriptionContext } = useSubscriptions();
	const location = useSelector((state) => state.location.location);

	const handleSelection = (selected) => {
		if (selected?.locationId !== selectedLocation?.locationId) {
			setOpen(!open);
			setSelectedLocation(selected);

			const locationId = selected.locationId;

			addSubscription(
				["groupAccessSettings", locationId],
				[
					{
						context: "onAddUserGroup",
						query: gql`
							subscription onAddUserGroup($locationId: String) {
								onAddUserGroup(locationId: $locationId) {
									_id
									locationId
									groupName
									groupDescription
									groupActions
								}
							}
						`,
						variables: {
							locationId,
						},
					},
					{
						context: "onUpdateUserGroup",
						query: gql`
							subscription onUpdateUserGroup($locationId: String) {
								onUpdateUserGroup(locationId: $locationId) {
									_id
									locationId
									groupName
									groupDescription
								}
							}
						`,
						variables: {
							locationId,
						},
					},
					{
						context: "onDeleteUserGroup",
						query: gql`
							subscription onDeleteUserGroup($locationId: String) {
								onDeleteUserGroup(locationId: $locationId) {
									_id
									locationId
								}
							}
						`,
						variables: {
							locationId,
						},
					},
					{
						context: "onAddUserGroupAccessActions",
						query: gql`
							subscription onAddUserGroupAccessActions($locationId: String) {
								onAddUserGroupAccessActions(locationId: $locationId) {
									_id
									locationId
									actions {
										_id
										actionDescription
										actionKey
										actionName
										category
									}
								}
							}
						`,
						variables: {
							locationId,
						},
					},
					{
						context: "onDeleteUserGroupAccessActions",
						query: gql`
							subscription onDeleteUserGroupAccessActions($locationId: String) {
								onDeleteUserGroupAccessActions(locationId: $locationId) {
									_id
									locationId
									actions {
										_id
										actionDescription
										actionKey
										actionName
										category
									}
								}
							}
						`,
						variables: {
							locationId,
						},
					},
					{
						context: "onAddUserGroupAccessUsers",
						query: gql`
							subscription onAddUserGroupAccessUsers($locationId: String) {
								onAddUserGroupAccessUsers(locationId: $locationId) {
									_id
									locationId
									users {
										_id
										fullName
										position
										locationId
										locationName
									}
								}
							}
						`,
						variables: {
							locationId,
						},
					},
					{
						context: "onDeleteUserGroupAccessUsers",
						query: gql`
							subscription onDeleteUserGroupAccessUsers($locationId: String) {
								onDeleteUserGroupAccessUsers(locationId: $locationId) {
									_id
									locationId
									users {
										_id
										fullName
										position
										locationId
										locationName
									}
								}
							}
						`,
						variables: {
							locationId,
						},
					},
				]
			);
		}
	};

	useEffect(() => {
		return () => {
			setOpen(true);
			setSelectedLocation(null);
			clearSubscriptionContext();
			clearCache(QUERY_IDENTIFIERS);
		};
	}, [location, clearSubscriptionContext]);

	return (
		<BodyLayer
			floatingContent={
				<CollapsibleLeftPanel
					permanentTrigger={open}
					open={true}
					control={
						<AppSideBar
							searchProps={{
								searchBy: "name",
								loading: isLoading,
							}}
							sideBarProps={{
								showBy: "name",
								loading: isLoading,
								tree: globalLocations,
								selected: selectedLocation?._id || "",
								onSelectAction: handleSelection,
							}}
						/>
					}
				/>
			}
		>
			<RoundedBox sx={{ minHeight: "630px" }}>
				<GroupAccessSettingsModel selectedLocation={selectedLocation} location={location} />
			</RoundedBox>
		</BodyLayer>
	);
}
